<template>
  <div class="bi-summary-section" :class="{ [variant]: !!variant }">
    <div class="__grid">
      <BIDisplayValue
        size="medium"
        :title="$t('page.business-intelligence.summary.success-rate.bids-submitted')"
        :value="data && data.current_period.bids_placed !== undefined ? data.current_period.bids_placed : undefined"
        :comparisonValue="
          data && data.previous_period.bids_placed !== undefined ? data.previous_period.bids_placed : undefined
        "
        :show-comparison-value="true"
        type="number"
        :previousPeriodFrom="data && data.previous_period.start_date"
        :previousPeriodTo="data && data.previous_period.end_date"
      />

      <BIDisplayValue
        size="medium"
        :title="$t('page.business-intelligence.summary.success-rate.bids-won')"
        :value="data && data.current_period.tenders_won !== undefined ? data.current_period.tenders_won : undefined"
        :comparisonValue="
          data && data.previous_period.tenders_won !== undefined ? data.previous_period.tenders_won : undefined
        "
        :show-comparison-value="true"
        type="number"
        :previousPeriodFrom="data && data.previous_period.start_date"
        :previousPeriodTo="data && data.previous_period.end_date"
      />
      <BIDisplayValue
        size="medium"
        :title="$t('page.business-intelligence.summary.bid-ratio.title')"
        :value="data && data.current_period.bid_ratio !== undefined ? data.current_period.bid_ratio : undefined"
        :comparisonValue="
          data && data.previous_period.bid_ratio !== undefined ? data.previous_period.bid_ratio : undefined
        "
        type="percentage"
        :show-comparison-value-in-tooltip="true"
        :previousPeriodFrom="data && data.previous_period.start_date"
        :previousPeriodTo="data && data.previous_period.end_date"
      >
        <template v-slot:tooltip>
          <div>
            {{
              $t('page.business-intelligence.summary.bid-ratio.current-period-converted', {
                converted: formatCount(data.current_period.potential_bids_converted),
                potential: formatCount(data.current_period.potential_bids),
              })
            }}
          </div>
          <div>
            {{
              $t('page.business-intelligence.summary.bid-ratio.rate-dates', {
                value: formatPercentageShort(data.current_period.bid_ratio),
                dates: formatPeriod(data.current_period.start_date, data.current_period.end_date),
              })
            }}
          </div>
          <div class="bi-summary-section__tooltip-spacer">
            {{
              $t('page.business-intelligence.summary.bid-ratio.previous-period-converted', {
                converted: formatCount(data.previous_period.potential_bids_converted),
                potential: formatCount(data.previous_period.potential_bids),
              })
            }}
          </div>
          <div>
            {{
              $t('page.business-intelligence.summary.bid-ratio.rate-dates', {
                value: formatPercentageShort(data.previous_period.bid_ratio),
                dates: formatPeriod(data.previous_period.start_date, data.previous_period.end_date),
              })
            }}
          </div>
        </template>
      </BIDisplayValue>
      <BIDisplayValue
        size="medium"
        :title="$t('page.business-intelligence.summary.success-rate.title')"
        :value="data && data.current_period.success_rate !== undefined ? data.current_period.success_rate : undefined"
        :comparisonValue="
          data && data.previous_period.success_rate !== undefined ? data.previous_period.success_rate : undefined
        "
        type="percentage"
        :show-comparison-value-in-tooltip="true"
        :previousPeriodFrom="data && data.previous_period.start_date"
        :previousPeriodTo="data && data.previous_period.end_date"
      />
      <BIDisplayValue
        size="medium"
        :title="$t('page.business-intelligence.summary.won-bids.min')"
        :value="data && data.current_period.lowest !== undefined ? data.current_period.lowest : undefined"
        :comparisonValue="data && data.previous_period.lowest !== undefined ? data.previous_period.lowest : undefined"
        type="currency"
        :show-comparison-value-in-tooltip="true"
        :previousPeriodFrom="data && data.previous_period.start_date"
        :previousPeriodTo="data && data.previous_period.end_date"
      />
      <BIDisplayValue
        size="medium"
        :title="$t('page.business-intelligence.summary.won-bids.median')"
        :value="data && data.current_period.median !== undefined ? data.current_period.median : undefined"
        :comparisonValue="data && data.previous_period.median !== undefined ? data.previous_period.median : undefined"
        type="currency"
        :show-comparison-value-in-tooltip="true"
        :previousPeriodFrom="data && data.previous_period.start_date"
        :previousPeriodTo="data && data.previous_period.end_date"
      />
      <BIDisplayValue
        size="medium"
        :title="$t('page.business-intelligence.summary.won-bids.max')"
        :value="data && data.current_period.highest !== undefined ? data.current_period.highest : undefined"
        :comparisonValue="data && data.previous_period.highest !== undefined ? data.previous_period.highest : undefined"
        type="currency"
        :show-comparison-value-in-tooltip="true"
        :previousPeriodFrom="data && data.previous_period.start_date"
        :previousPeriodTo="data && data.previous_period.end_date"
      />
      <BIDisplayValue
        size="medium"
        :title="$t('page.business-intelligence.summary.cumulative-bids.title')"
        :value="
          data && data.current_period.bids_won_total !== undefined ? data.current_period.bids_won_total : undefined
        "
        :show-comparison-value="true"
        :comparisonValue="
          data && data.previous_period.bids_won_total !== undefined ? data.previous_period.bids_won_total : undefined
        "
        type="currency"
        :previousPeriodFrom="data && data.previous_period.start_date"
        :previousPeriodTo="data && data.previous_period.end_date"
      />
    </div>

    <!-- add the edit button at the end of the last section -->
    <BaseRoundButton
      v-if="allowEdit && !pending"
      class="edit-button"
      icon="edit-2"
      :size="30"
      :disabled="pending"
      @click.native="onEditClicked"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { calculatePercentage, formatLocalizedNumber, formatPercentageShort } from '@/util/number-util';
import { formatCurrencyShort } from '@/util/currency-util';
import { formatStandardDate, toMoment } from '@/util/date-util';
import BIDisplayValue from './BIDisplayValue.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';

export default {
  name: 'BISummarySection',
  components: {
    BIDisplayValue,
    BaseRoundButton,
  },
  props: {
    data: {
      type: Object,
    },
    variant: {
      type: String,
      required: false,
      default: 'default', // highlighted | default
    },
    allowEdit: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapGetters('biDashboard', ['pending']),
  },
  methods: {
    calculatePercentage,
    formatCurrencyShort,
    formatPercentageShort,
    toMoment,
    formatCount(value) {
      if (value === null || value === undefined) {
        return '-';
      }
      return formatLocalizedNumber(value);
    },
    formatPeriod(from, to) {
      if (from && to) {
        return `${formatStandardDate(from)} - ${formatStandardDate(to)}`;
      }
      return '';
    },
    onEditClicked() {
      this.$emit('on-edit-clicked');
    },
  },
};
</script>

<style lang="scss">
.bi-summary-section {
  position: relative;
  display: grid;
  // Never wider than its box, a phone's included.
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  grid-gap: 1rem;

  &.highlighted {
    background-color: $color-accent-pale;
  }

  .edit-button {
    position: absolute;
    top: -10px;
    right: -10px;
  }

  .__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
    gap: 12px;
    align-items: start;
  }
}

// rendered inside the portaled popover, so it must be a top-level selector
.bi-summary-section__tooltip-spacer {
  margin-top: 8px;
}
</style>
